import React, { useEffect, useMemo, useState } from "react";
import { Calculator, StickyNote, Copy } from "lucide-react";
import JeaCard from "@/components/jea/JeaCard";
import JeaButton from "@/components/jea/JeaButton";
import Skeleton from "@/components/jea/Skeleton";
import { demoClient, useDemo } from "@/pages/demo/DemoShell";
import { formatMoney } from "@/lib/pricing";
import { cn } from "@/lib/utils";

const daysBetween = (from) => {
  if (!from) return 0;
  const start = new Date(from);
  const now = new Date();
  return Math.max(0, Math.floor((now - start) / (1000 * 60 * 60 * 24)));
};

const DemoInterestCalculatorPage = () => {
  const demo = useDemo();
  const [debtors, setDebtors] = useState([]);
  const [debtorId, setDebtorId] = useState(null);
  const [rate, setRate] = useState(9);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await demoClient.get("/demo/debtors");
        setDebtors(data.debtors || []);
        if (data.debtors?.length) {
          setDebtorId(data.debtors[0].id);
          setRate(data.debtors[0].judgment?.interest_rate ?? 9);
        }
      } finally { setLoading(false); }
    })();
  }, []);

  const debtor = debtors.find((d) => d.id === debtorId);

  const result = useMemo(() => {
    if (!debtor?.judgment) return null;
    const principal = debtor.judgment.amount_cents || 0;
    const days = daysBetween(debtor.judgment.date_entered);
    const yearlyRate = (rate || 0) / 100;
    const dailyRate = yearlyRate / 365;
    const accrued = Math.round(principal * dailyRate * days);
    return { principal, days, accrued, total: principal + accrued };
  }, [debtor, rate]);

  const nudge = () => demo?.openSignUp("saving interest calculations to notes");

  return (
    <div className="space-y-6" data-testid="demo-interest-page">
      <div>
        <div className="jea-caption">Statutory + Contractual Interest</div>
        <h1 className="text-[28px] font-bold text-white">Interest Calculator</h1>
        <p className="text-text-secondary text-[14px] mt-1">
          Compound daily. State-specific rates auto-applied. Save the calculation directly to the debtor's notes.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6" data-tour="interest-panel">
        <JeaCard>
          <div className="jea-caption mb-3">Inputs</div>
          {loading ? <Skeleton className="h-40" /> : (
            <div className="space-y-4">
              <div>
                <div className="jea-caption mb-2">Debtor</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {debtors.slice(0, 6).map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => { setDebtorId(d.id); setRate(d.judgment?.interest_rate ?? 9); }}
                      className={cn(
                        "text-left rounded-[8px] border p-3 transition-all",
                        d.id === debtorId
                          ? "border-accent-pink bg-accent-pink/10"
                          : "border-border-default hover:border-accent-pink/40"
                      )}
                      data-testid={`demo-interest-debtor-${d.id}`}
                    >
                      <div className="text-white text-[13px] font-semibold truncate">{d.full_name}</div>
                      <div className="text-text-muted text-[11px]">
                        {formatMoney(d.judgment?.amount_cents || 0)} · {d.judgment?.state}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="jea-caption block mb-2">Interest Rate (annual %)</label>
                <input
                  type="number"
                  min="0" max="30" step="0.25"
                  value={rate}
                  onChange={(e) => setRate(parseFloat(e.target.value) || 0)}
                  className="h-11 w-32 rounded-[8px] bg-bg-tertiary border border-border-default focus:border-accent-pink focus:shadow-pink-glow px-3 text-white jea-mono focus:outline-none transition-all"
                  data-testid="demo-interest-rate"
                />
                <span className="text-text-secondary text-[12px] ml-3">
                  State default for {debtor?.judgment?.state || "—"}: {debtor?.judgment?.interest_rate ?? "—"}%
                </span>
              </div>
            </div>
          )}
        </JeaCard>

        <JeaCard data-testid="demo-interest-result">
          <div className="jea-caption mb-3 flex items-center gap-2"><Calculator size={13} /> Result</div>
          {result ? (
            <>
              <div className="space-y-2 text-[13px]">
                <Row label="Principal"        value={formatMoney(result.principal)} />
                <Row label="Days Since Entry" value={`${result.days} days`} />
                <Row label="Rate"             value={`${rate}% / yr`} />
                <Row label="Accrued Interest" value={formatMoney(result.accrued)} accent />
              </div>
              <div className="mt-4 pt-4 border-t border-border-default">
                <div className="jea-caption">Total Owed Today</div>
                <div className="jea-mono text-accent-pink font-extrabold text-[26px] mt-1">
                  {formatMoney(result.total)}
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <JeaButton size="sm" variant="outline-pink" className="gap-2" onClick={nudge} data-testid="demo-interest-save-note">
                  <StickyNote size={14} /> Save to Notes
                </JeaButton>
                <JeaButton size="sm" variant="outline-neutral" className="gap-2" onClick={() => {
                  try { navigator.clipboard?.writeText(`Interest calc — ${formatMoney(result.total)} owed`); } catch {}
                }} data-testid="demo-interest-copy">
                  <Copy size={14} /> Copy
                </JeaButton>
              </div>
            </>
          ) : <Skeleton className="h-32" />}
        </JeaCard>
      </div>
    </div>
  );
};

const Row = ({ label, value, accent }) => (
  <div className="flex justify-between items-center">
    <span className="text-text-secondary">{label}</span>
    <span className={cn("jea-mono font-bold", accent ? "text-accent-pink" : "text-white")}>{value}</span>
  </div>
);

export default DemoInterestCalculatorPage;
